import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router, UrlTree } from '@angular/router';
import { AuthService } from '../auth/auth.service';
import { RouteAccessNoticeService } from './route-access-notice.service';
import {
  landingRouteForRole,
  resolveWorkspaceRoleFromTokenRoles,
  roleDefinition,
  roleIdForDashboardRoute,
  type DashboardRouteData,
  type WorkspaceRoleId,
} from './workspace-role.model';
import { WorkspaceRoleService } from './workspace-role.service';

function tokenRoleForGuard(
  auth: AuthService,
  roleService: WorkspaceRoleService,
): WorkspaceRoleId | null {
  const cached = roleService.resolvedTokenRoleId();
  if (cached) {
    return cached;
  }

  const roles = auth.getUserSession()?.roles ?? [];
  const resolved = resolveWorkspaceRoleFromTokenRoles(roles);
  if (resolved) {
    roleService.applyRoleFromKeycloak(roles);
  }
  return resolved;
}

function blockedAsUnmapped(router: Router): UrlTree {
  return router.createUrlTree(['/account-blocked'], { queryParams: { reason: 'unmapped' } });
}

function pathOf(url: string): string {
  return url.split('?')[0].split('#')[0];
}

/**
 * Roles allowed on a route: an explicit `data.allowedRoles` allow-list when the
 * route declares one (CCWIS-174 / CCWIS-220: /dashboard is Intake Specialist,
 * Administrator, and Supervisor — supervisor confirms type + priority and
 * assigns the investigator), otherwise the single role that owns the dashboard.
 * An empty list means the route is not role-scoped.
 */
function allowedRolesFor(route: ActivatedRouteSnapshot, url: string): readonly WorkspaceRoleId[] {
  const declared = (route.data as DashboardRouteData | undefined)?.allowedRoles;
  if (declared) {
    return declared;
  }

  const owner = roleIdForDashboardRoute(url);
  return owner ? [owner] : [];
}

function deniedMessage(allowedRoles: readonly WorkspaceRoleId[]): string {
  const labels = allowedRoles.map((id) => roleDefinition(id).label);
  const audience =
    labels.length > 1
      ? `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`
      : labels[0];

  return `This page is for ${audience}. You were taken to your own dashboard.`;
}

/**
 * Fail-closed route gate: the access token's actor role decides which
 * dashboards open, before the page paints. The demo role switcher, the URL,
 * and localStorage `ucwip.workspaceRole` never grant access (CCWIS-171 / 174).
 */
export const workspaceRoleGuard: CanActivateFn = async (route, state) => {
  const auth = inject(AuthService);
  const roleService = inject(WorkspaceRoleService);
  const notice = inject(RouteAccessNoticeService);
  const router = inject(Router);

  await auth.ensureInitialized();

  // Storybook and component tests mount these screens without a token.
  if (!auth.isAuthenticated()) {
    return true;
  }

  if (auth.accountBlocked()) {
    return router.createUrlTree(['/account-blocked']);
  }

  const tokenRole = tokenRoleForGuard(auth, roleService);
  if (!tokenRole) {
    return blockedAsUnmapped(router);
  }

  const allowedRoles = allowedRolesFor(route, state.url);
  if (allowedRoles.length === 0 || allowedRoles.includes(tokenRole)) {
    return true;
  }

  const home = landingRouteForRole(tokenRole);

  // No home, or a home that would itself be denied: stop rather than ping-pong.
  if (!home || home === pathOf(state.url)) {
    return blockedAsUnmapped(router);
  }

  notice.deny(deniedMessage(allowedRoles), home);
  return router.createUrlTree([home]);
};

/** Post-login landing: the token role's own home, not always the intake list. */
export const workspaceLandingGuard: CanActivateFn = async () => {
  const auth = inject(AuthService);
  const roleService = inject(WorkspaceRoleService);
  const router = inject(Router);

  await auth.ensureInitialized();

  if (!auth.isAuthenticated()) {
    return router.createUrlTree([roleService.dashboardRoute()]);
  }

  if (auth.accountBlocked()) {
    return router.createUrlTree(['/account-blocked']);
  }

  const tokenRole = tokenRoleForGuard(auth, roleService);
  const home = tokenRole ? landingRouteForRole(tokenRole) : null;

  return home ? router.createUrlTree([home]) : blockedAsUnmapped(router);
};
